import { useEffect, useMemo, useState } from "react";
import apiCliente from "../../../services/apiCliente";

/**
 * Descobre quais telas do portal existem de fato para o cliente logado.
 *
 * Uma rota só aparece no menu (pages/client/layout) e só pode ser liberada em uma
 * permissão (pages/client/administracao/permissoes) se o cliente tiver a
 * infraestrutura/configuração correspondente — não adianta liberar "ONUs" para
 * um cliente que não tem ONU.
 *
 * Fonte única dessas regras: qualquer tela que precise saber o que existe para o
 * cliente deve consumir este hook em vez de refazer as requisições.
 */
export default function useRotasDisponiveisCliente() {
  const erp = window?.config?.REACT_APP_ERP || process.env.REACT_APP_ERP || "";
  const isFinanceHidden =
    window?.config?.REACT_APP_FINANCE_CLIENT ||
    process.env.REACT_APP_FINANCE_CLIENT;
  const isNf = erp === "HUBSOFT";

  const [infraAvailability, setInfraAvailability] = useState({
    cpes: false,
    datacom: false,
    onus: false,
    linksSessions: false,
    flow: false,
    firewall: false,
  });
  const [loadingInfra, setLoadingInfra] = useState(true);
  const [isGrupo, setIsGrupo] = useState(false);
  const [downloadReportPermission, setDownloadReportPermission] =
    useState(false);
  const [hasSuggestionForm, setHasSuggestionForm] = useState(false);
  const [loadingExtras, setLoadingExtras] = useState(true);

  // Rotas que o interno liberou para o portal do cliente (independe de
  // permissão — vale até para o ADM, que não passa pela checagem de rota por
  // permissão). Uma rota desabilitada aqui pelo interno some do menu para
  // TODOS os usuários do cliente, sem exceção.
  const [rotasHabilitadasInterno, setRotasHabilitadasInterno] = useState(null);
  const [loadingRotasInterno, setLoadingRotasInterno] = useState(true);

  useEffect(() => {
    let ativo = true;

    async function fetchInfraAvailability() {
      try {
        const payload = {
          includeEmptyHosts: true,
          mapHostsByServiceAndEquipmentType: true,
        };
        const response = await apiCliente.post(
          "cliente/findMany/hosts",
          payload,
        );
        const { cpes, onus, links, sessions } = response.data || {};

        const hasCpes =
          cpes && Object.values(cpes).some((arr) => arr.length > 0);

        let hasDatacom = false;
        if (cpes) {
          hasDatacom = Object.values(cpes).some((arr) =>
            arr.some((item) => item?.device?.toLowerCase() === "datacom"),
          );
        }

        // Detecta hosts Firewall (fabricante === "Fortinet") dentro dos cpes
        let hasFirewall = false;
        if (cpes) {
          hasFirewall = Object.values(cpes).some((arr) =>
            arr.some((item) => item?.fabricante?.toLowerCase() === "fortinet"),
          );
        }

        const hasOnus =
          onus && Object.values(onus).some((arr) => arr.length > 0);

        const hasLinksSessions =
          (links && Object.values(links).some((arr) => arr.length > 0)) ||
          (sessions && Object.values(sessions).some((arr) => arr.length > 0));

        let hasFlow = false;
        try {
          const flowResponse = await apiCliente.get(
            "cliente/dashHome/checkFlowAvailability",
          );
          hasFlow = flowResponse?.data?.hasFlow || false;
        } catch (err) {
          console.error("Erro ao verificar flow:", err);
        }

        if (!ativo) return;

        setInfraAvailability({
          cpes: !!hasCpes,
          datacom: !!hasDatacom,
          onus: !!hasOnus,
          linksSessions: !!hasLinksSessions,
          flow: hasFlow,
          firewall: !!hasFirewall,
        });
      } catch (error) {
        console.error("Erro ao buscar hosts da infraestrutura:", error);
      } finally {
        if (ativo) setLoadingInfra(false);
      }
    }

    fetchInfraAvailability();

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (erp !== "IXC") return;

    let ativo = true;

    async function fetchEmpresasGrupo() {
      try {
        const response = await apiCliente.post("/cliente/find/empresasGrupo");
        if (ativo) setIsGrupo(!!response?.data?.isGrupo);
      } catch (error) {
        console.error("Erro ao buscar empresas do grupo econômico:", error);
      }
    }

    fetchEmpresasGrupo();

    return () => {
      ativo = false;
    };
  }, [erp]);

  useEffect(() => {
    let ativo = true;

    const fetchRotasHabilitadas = async () => {
      try {
        const response = await apiCliente.get("/cliente/find/rotasHabilitadas");
        if (ativo) setRotasHabilitadasInterno(new Set(response?.data || []));
      } catch (error) {
        console.error("Erro ao buscar rotas habilitadas pelo interno:", error);
        // Falha aqui não pode liberar tudo por engano: mantém um Set vazio,
        // que resulta em nenhuma rota disponível até a próxima tentativa
        if (ativo) setRotasHabilitadasInterno(new Set());
      } finally {
        if (ativo) setLoadingRotasInterno(false);
      }
    };

    fetchRotasHabilitadas();

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    let ativo = true;

    const fetchExtras = async () => {
      const result = JSON.parse(localStorage.getItem("userCliente"));

      let hasPermission = false;
      try {
        hasPermission =
          !!result?.usuarioCliente?.cliente?.ReportSettings?.[0]?.permission;
        if (ativo) setDownloadReportPermission(hasPermission);
      } catch (error) {
        console.error("Error checking report permission:", error);
        if (ativo) setDownloadReportPermission(false);
      }

      if (!hasPermission) {
        try {
          const response = await apiCliente.get(
            "/cliente/relatorios/publicados",
          );
          if (ativo && response?.data && response.data.length > 0) {
            setDownloadReportPermission(true);
          }
        } catch (error) {
          console.error("Error checking published reports count:", error);
        }
      }

      try {
        const response = await apiCliente.get("/client/find/suggestionForm");
        if (!ativo) return;
        if (
          response?.data &&
          !response.data.noActiveForm &&
          (response.data.id || response.data.alreadyAnswered)
        ) {
          setHasSuggestionForm(true);
        } else {
          setHasSuggestionForm(false);
        }
      } catch (error) {
        console.error("Error checking suggestion form:", error);
        if (ativo) setHasSuggestionForm(false);
      } finally {
        if (ativo) setLoadingExtras(false);
      }
    };

    fetchExtras();

    return () => {
      ativo = false;
    };
  }, []);

  // Mapa { path: true } com as telas que existem para este cliente
  const rotasDisponiveis = useMemo(() => {
    const dashboard = {
      "/home/overview": infraAvailability.cpes,
      "/home/saude": infraAvailability.cpes,
      "/home/disponibilidade": infraAvailability.cpes,
      "/home/firewall": infraAvailability.firewall,
      "/home/dash/sfp": infraAvailability.cpes,
      "/home/onu": infraAvailability.onus,
      "/home/rede": infraAvailability.linksSessions,
      "/home/flow": infraAvailability.flow,
    };

    const temDashboard = Object.values(dashboard).some(Boolean);
    const temFinanceiro = !isFinanceHidden;

    const mapa = {
      "/resumo": true,
      "/home": temDashboard,
      ...dashboard,
      "/tickets": true,
      "/financeiro": temFinanceiro,
      "/financeiro/faturas": temFinanceiro,
      "/financeiro/nfs": temFinanceiro && isNf,
      "/financeiro/contratos":
        temFinanceiro && (erp === "VERO" || (erp === "IXC" && isGrupo)),
      "/relatorios": downloadReportPermission,
      "/sugestoes": hasSuggestionForm,
    };

    // Rota só conta como disponível se: (a) o cliente tem a infra/config pra
    // ela E (b) o interno não a desabilitou globalmente. (b) vale até pra ADM.
    return Object.fromEntries(
      Object.entries(mapa).filter(
        ([path, disponivel]) =>
          !!disponivel && !!rotasHabilitadasInterno?.has(path),
      ),
    );
  }, [
    infraAvailability,
    isFinanceHidden,
    isNf,
    erp,
    isGrupo,
    downloadReportPermission,
    hasSuggestionForm,
    rotasHabilitadasInterno,
  ]);

  return {
    loading: loadingInfra || loadingExtras || loadingRotasInterno,
    loadingInfra,
    rotasDisponiveis,
    infraAvailability,
    isGrupo,
    downloadReportPermission,
    hasSuggestionForm,
  };
}
